import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { rfpApi } from '../../services/rfpApi';
import { DocTable, ScoreTableFile } from '../../types/rfp';

/**
 * 배점표 — 공고 첨부(한글 문서)에 있는 표를 **그대로** 보여 준다. AI 를 쓰지 않는다(2026-10-16).
 *
 * 배점표는 협상에 의한 계약 · 설계공모처럼 제안서를 채점하는 공고에만 있다. **없으면 칸을 아예 그리지 않는다** —
 * 「배점표 없음」 안내도 두지 않는다(수의 · 최저가 · 적격심사 공고가 많아 늘 비어 보였다).
 * PDF 첨부는 표를 읽을 수 없어 이 칸에 나오지 않는다 — 공고 원문에서 확인한다.
 *
 * **첫 표만 펼치고 나머지는 이름 줄로 접는다**(2026-11-02) — 공고문 · 제안요청서에 같은 평가표가 모양만 바꿔
 * 여러 번 들어 있어(N2SF 건 표 6개) 화면이 길었다. 이름 줄을 누르면 그 표가 펼쳐진다.
 */
export const ScoreTablesPanel: React.FC<{ projectId: number }> = ({ projectId }) => {
  const [files, setFiles] = useState<ScoreTableFile[] | null>(null);
  /** 펼친 표 — 처음엔 첫 표만 */
  const [open, setOpen] = useState<Set<string>>(new Set());

  useEffect(() => {
    let gone = false;
    rfpApi.scoreTables(projectId).then((x) => { if (!gone) setFiles(x); }).catch(() => { if (!gone) setFiles([]); });
    return () => { gone = true; };
  }, [projectId]);

  if (!files || files.length === 0) return null;

  const tables = files.flatMap((f) => f.tables.map((t, i) => ({
    key: `${f.fileId}-${i}`,
    label: `${f.fileName}${f.tables.length > 1 ? ` · 표 ${i + 1}` : ''}`,
    t,
  })));
  const isOpen = (key: string, idx: number) => (idx === 0 ? !open.has(key) : open.has(key)); // 첫 표는 누르면 접힌다
  const toggle = (key: string) => setOpen((s) => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });

  return (
    <section className="bg-white rounded-lg border border-slate-200">
      <div className="px-4 py-3 border-b border-slate-100">
        <h2 className="text-sm font-semibold text-slate-900">
          배점표 {tables.length > 1 && <span className="font-normal text-slate-400">표 {tables.length}개 · 이름을 누르면 펼칩니다</span>}
        </h2>
      </div>
      <div className="p-4 space-y-3">
        {tables.map(({ key, label, t }, idx) => {
          const on = isOpen(key, idx);
          return (
            <div key={key}>
              <button
                type="button"
                onClick={() => toggle(key)}
                aria-expanded={on}
                className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800"
              >
                {on ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
                {label}
              </button>
              {on && (
                <div className="mt-1.5 overflow-x-auto">
                  <Table t={t} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

/** 칸 주소 · 병합대로 그린다 — 각 행에서 그 행에서 시작하는 칸만 낸다 */
const Table: React.FC<{ t: DocTable }> = ({ t }) => {
  const rows = Array.from({ length: t.rows }, (_, r) => t.cells.filter((c) => c.row === r).sort((a, b) => a.col - b.col));
  const isNum = (s: string) => /^[+\-＋]?\d+(\.\d+)?\s*점?$/.test(s.trim());
  return (
    <table className="min-w-full border-collapse text-xs text-slate-700">
      <tbody>
        {rows.map((cells, r) => (
          <tr key={r} className={r === 0 ? 'bg-slate-50 font-medium text-slate-900' : ''}>
            {cells.map((c) => (
              <td key={c.col} rowSpan={c.rowSpan} colSpan={c.colSpan}
                className={`border border-slate-200 px-2 py-1.5 align-top whitespace-pre-line ${isNum(c.text) ? 'text-center tabular-nums font-medium' : ''}`}>
                {c.text}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};
